import { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Link as LinkIcon, Type, Check, Blocks, X } from 'lucide-react';
import { Button, Modal, TabbedInterface } from '@ai-tutor/ui';
import {
  type AttachSourceModalProps,
  type AttachTabId,
  type StagedSource,
} from './types';
import { UploadFileTab, type TabFocusHandle } from './tabs/UploadFileTab';
import { AddLinkTab } from './tabs/AddLinkTab';
import { PasteTextTab } from './tabs/PasteTextTab';
import {
  AttachSourceSheet,
  SESSION_ICON,
  type SessionEntry,
} from './AttachSourceSheet';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

const TAB_LABELS: Record<AttachTabId, string> = {
  file: 'Upload File',
  link: 'Add Link',
  text: 'Paste Text',
};

/** Inline label for the disabled Integrations tab - `TabbedInterface.label`
 *  is typed as `string`, but React renders children fine if we pass JSX.
 *  We cast at the assignment site so the rest of the tab config stays typed. */
const INTEGRATIONS_LABEL = (
  <span className='inline-flex items-center gap-1.5'>
    Integrations
    <span className='dark:tint-brand-strong rounded-full bg-brand-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-600'>
      Soon
    </span>
  </span>
) as unknown as string;

function summarize(source: StagedSource): SessionEntry {
  if (source.kind === 'file') return { kind: 'file', label: source.file.name };
  if (source.kind === 'link')
    return { kind: 'link', label: source.name || source.url };
  return { kind: 'text', label: source.name };
}

export function AttachSourceModal({
  isOpen,
  onClose,
  onAdd,
  onRemove,
  remainingFileSlots,
  initialTab = 'file',
}: AttachSourceModalProps) {
  const [activeTab, setActiveTab] = useState<AttachTabId>(initialTab);
  const [sessionAdds, setSessionAdds] = useState<SessionEntry[]>([]);

  const fileTabRef = useRef<TabFocusHandle>(null);
  const linkTabRef = useRef<TabFocusHandle>(null);
  const textTabRef = useRef<TabFocusHandle>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  // Below `sm` this is a different component, not a narrower one: a bottom
  // sheet that starts with the three ways in (`AttachSourceSheet`). The tab
  // strip and drop zone are desktop furniture.
  const isWide = useMediaQuery('(min-width: 640px)');
  const labels = TAB_LABELS;

  // Reset state each time the modal opens so a new session starts clean.
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setSessionAdds([]);
    }
  }, [isOpen, initialTab]);

  // Wrap the caller's onAdd so we can log a confirmation chip per
  // successful add. Errors thrown by onAdd propagate to the tab so it can
  // surface them inline - we only log on success.
  const handleAdd = useCallback(
    async (source: StagedSource) => {
      await onAdd(source);
      setSessionAdds(prev => [...prev, summarize(source)]);
    },
    [onAdd]
  );

  // a11y: snapshot the trigger on open, restore focus on close, focus the
  // active tab's primary input on open + on tab switch.
  useEffect(() => {
    if (!isOpen) {
      triggerRef.current?.focus();
      triggerRef.current = null;
      return;
    }
    triggerRef.current = document.activeElement as HTMLElement | null;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => {
      const target =
        activeTab === 'file'
          ? fileTabRef.current
          : activeTab === 'link'
            ? linkTabRef.current
            : textTabRef.current;
      target?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, activeTab]);

  const removeEntry = (i: number) => {
    const entry = sessionAdds[i];
    if (!entry) return;
    // Index within its own kind, so the caller can find the right slot in
    // its file / link / text arrays without the modal holding full state.
    const indexInKind = sessionAdds
      .slice(0, i)
      .filter(e => e.kind === entry.kind).length;
    setSessionAdds(prev => prev.filter((_, j) => j !== i));
    onRemove?.(entry.kind, indexInKind);
  };

  if (!isWide) {
    return (
      <AttachSourceSheet
        isOpen={isOpen}
        onClose={onClose}
        onAdd={handleAdd}
        remainingFileSlots={remainingFileSlots}
        initialTab={initialTab}
        sessionAdds={sessionAdds}
        onRemoveEntry={removeEntry}
      />
    );
  }

  const tabs = [
    {
      id: 'file' as const,
      label: labels.file,
      icon: Upload,
      content: (
        <UploadFileTab
          ref={fileTabRef}
          onAdd={handleAdd}
          remainingFileSlots={remainingFileSlots}
        />
      ),
    },
    {
      id: 'link' as const,
      label: labels.link,
      icon: LinkIcon,
      content: <AddLinkTab ref={linkTabRef} onAdd={handleAdd} />,
    },
    {
      id: 'text' as const,
      label: labels.text,
      icon: Type,
      content: <PasteTextTab ref={textTabRef} onAdd={handleAdd} />,
    },
    {
      id: 'integrations' as const,
      label: INTEGRATIONS_LABEL,
      icon: Blocks,
      disabled: true,
      content: null,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Attach source'
      size='xl'
      overlayClassName='bg-black/30 backdrop-blur-sm'
      contentClassName='border border-border bg-card shadow-xl'
    >
      {/*
        Pin a fixed body height so the modal doesn't jump when the user
        switches tabs (Paste Text is much taller than Add Link otherwise).
        Sized to fit the tallest tab (PasteText with a 10-row textarea).
        Capped to the screen so a short phone, or one with the keyboard up,
        cannot push the Done footer below the fold; the tab body scrolls.
      */}
      <div
        role='dialog'
        aria-modal='true'
        aria-label='Attach source'
        className='flex h-[min(540px,calc(100dvh-10rem))] flex-col'
      >
        <TabbedInterface
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={id => setActiveTab(id as AttachTabId)}
          variant='underline'
          showCounts={false}
          tabsClassName='px-4'
          className='min-h-0 flex-1'
          contentClassName='overflow-y-auto'
        />

        {/* Persistent footer - always visible. Holds the session-log chips
            on the left (when sources have been added) and the Done button
            on the right (always). Done closes the modal regardless of state. */}
        <div className='flex-none border-t border-border px-4 py-3'>
          {sessionAdds.length > 0 && (
            <div className='mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground'>
              <Check className='h-3.5 w-3.5 text-green-600 dark:text-green-400' />
              Added in this session ({sessionAdds.length})
            </div>
          )}
          <div className='flex items-center gap-3'>
            <div
              className='flex min-w-0 flex-1 gap-2 overflow-x-auto'
              style={{ scrollbarWidth: 'none' }}
            >
              {sessionAdds.map((entry, i) => {
                const Icon = SESSION_ICON[entry.kind];
                return (
                  <div
                    key={`${entry.kind}-${i}`}
                    className='flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card py-1.5 pl-3 pr-1 text-xs sm:py-1'
                  >
                    <Icon className='h-3 w-3 text-muted-foreground' />
                    <span className='max-w-[180px] truncate text-muted-foreground'>
                      {entry.label}
                    </span>
                    <button
                      type='button'
                      aria-label={`Remove ${entry.label}`}
                      onClick={() => removeEntry(i)}
                      // 32px on a phone (20px from `sm`) with a 44px `tap-target`; the
                      // chip grows to 44 tall (`py-1.5`) so the area fits
                      // inside it - the chip row scrolls sideways and would
                      // clip anything taller. Same treatment as ResourcePill.
                      className='tap-target ml-1 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 sm:h-5 sm:w-5'
                    >
                      <X className='h-3 w-3' />
                    </button>
                  </div>
                );
              })}
            </div>
            <Button
              type='button'
              size='sm'
              onClick={onClose}
              // 44px on a phone (the touch target), the `sm` size's 32px above.
              className='h-11 min-w-[120px] flex-none px-8 sm:h-8'
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
